import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button, CustomSelect, PublishModal, RichTextEditor, SaveDraftModal, Toggle } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import { usePublication, useCreatePublication, useUpdatePublication, usePublicationCategories } from '@/lib/api/hooks/publication';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { PublicationFormSchema, type PublicationCategory } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';

type PublicationFormInput = z.input<typeof PublicationFormSchema>;
type PublicationFormOutput = z.output<typeof PublicationFormSchema>;

const DEFAULT_FORM_VALUES: PublicationFormInput = {
  title: '',
  description: '',
  publicationCategory: '',
  sourceType: 'UPLOAD',
  fileUrl: '',
  externalLink: '',
  homePageKeyResources: false,
  coverImageUrl: '',
};

const FALLBACK_CATEGORY_OPTIONS = [{ value: 'GENERAL', label: 'General' }];

const SOURCE_TYPE_OPTIONS = [
  { value: 'UPLOAD', label: 'Upload File' },
  { value: 'EXTERNAL', label: 'External Link' },
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)}kb`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}mb`;
};

const getFileNameFromUrl = (url: string, fallback: string): string => {
  if (!url) {
    return fallback;
  }

  try {
    const raw = decodeURIComponent(url.split('?')[0].split('#')[0]);
    const segments = raw.split('/').filter(Boolean);
    return segments[segments.length - 1] || fallback;
  } catch {
    return fallback;
  }
};

const mapPublicationToFormValues = (publication?: Record<string, unknown>): PublicationFormInput => {
  if (!publication) {
    return DEFAULT_FORM_VALUES;
  }

  const isExternalSource = Boolean(publication.isExternalSource);

  return {
    ...DEFAULT_FORM_VALUES,
    title: String(publication.title || ''),
    description: String(publication.description || ''),
    publicationCategory: String(publication.publicationCategory || ''),
    sourceType: isExternalSource ? 'EXTERNAL' : 'UPLOAD',
    fileUrl: isExternalSource ? '' : String(publication.publicationUrl || ''),
    externalLink: isExternalSource ? String(publication.externalLink || '') : '',
    homePageKeyResources: Boolean(publication.homePageKeyResources),
    coverImageUrl: String(publication.coverImageUrl || ''),
  };
};

const PublicationEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = Boolean(id) && location.pathname.includes('/edit');
  const publicationId = isEditMode ? id : '';

  const { data: publicationData, isLoading: isLoadingPublication } = usePublication(publicationId || '');
  const { data: categoriesData, isLoading: isLoadingCategories } = usePublicationCategories();

  const { mutateAsync: createPublication, isPending: isCreating } = useCreatePublication();
  const { mutateAsync: updatePublication, isPending: isUpdating } = useUpdatePublication();
  const fileUploadMutation = useFileUpload();

  const documentUploadRef = useRef<HTMLDivElement>(null);
  const coverUploadRef = useRef<HTMLDivElement>(null);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [documentUploadKey, setDocumentUploadKey] = useState(0);
  const [coverUploadKey, setCoverUploadKey] = useState(0);
  const [localDocumentMeta, setLocalDocumentMeta] = useState<{ name: string; size: number } | null>(null);
  const [existingDocumentName, setExistingDocumentName] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<PublicationFormInput, unknown, PublicationFormOutput>({
    resolver: zodResolver(PublicationFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'all',
  });

  const sourceType = watch('sourceType');
  const coverImageUrl = watch('coverImageUrl');
  const fileUrl = watch('fileUrl');
  const title = watch('title');

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isUploadingDocument || isUploadingCover;

  useEffect(() => {
    if (isEditMode && publicationData?.data) {
      const nextValues = mapPublicationToFormValues(publicationData.data as Record<string, unknown>);
      reset(nextValues);

      if (nextValues.fileUrl) {
        setExistingDocumentName(getFileNameFromUrl(nextValues.fileUrl, `${nextValues.title || 'publication'}.pdf`));
      } else {
        setExistingDocumentName('');
      }
    }

    if (!isEditMode) {
      reset(DEFAULT_FORM_VALUES);
      setExistingDocumentName('');
    }

    setLocalDocumentMeta(null);
    setDocumentUploadKey((prev) => prev + 1);
    setCoverUploadKey((prev) => prev + 1);
  }, [isEditMode, publicationData, reset]);

  const categoryOptions = useMemo(() => {
    const categories = categoriesData?.data ?? [];
    if (!categories.length) {
      return FALLBACK_CATEGORY_OPTIONS;
    }

    return categories.map((cat: PublicationCategory) => {
          return {
        value: cat.value,
        label: cat.displayName,
      };
    });
  }, [categoriesData]);

  const handleBrowseDocument = useCallback(() => {
    documentUploadRef.current?.click();
  }, []);

  const handleBrowseCover = useCallback(() => {
    coverUploadRef.current?.click();
  }, []);

  const handleDocumentFileChange = useCallback(async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    try {
      setIsUploadingDocument(true);
      const uploadResult = await fileUploadMutation.mutateAsync({ file, category: 'DOCUMENT' });
      const uploadedUrl = uploadResult?.data?.fileUrl || '';

      if (!uploadedUrl) {
        throw new Error('File upload failed.');
      }

      setValue('fileUrl', uploadedUrl, { shouldDirty: true, shouldValidate: true });
      setLocalDocumentMeta({ name: file.name, size: file.size });
      setExistingDocumentName(file.name);
      setDocumentUploadKey((prev) => prev + 1);
      showToast.success('File uploaded successfully');
    } catch (error) {
      console.error(error);
      showToast.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploadingDocument(false);
    }
  }, [fileUploadMutation, setValue]);

  const handleRemoveDocument = useCallback(() => {
    setValue('fileUrl', '', { shouldDirty: true, shouldValidate: true });
    setLocalDocumentMeta(null);
    setExistingDocumentName('');
    setDocumentUploadKey((prev) => prev + 1);
  }, [setValue]);

  const handleCoverImageChange = useCallback(async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    try {
      setIsUploadingCover(true);
      const uploadResult = await fileUploadMutation.mutateAsync({ file, category: 'IMAGE' });
      const uploadedUrl = uploadResult?.data?.fileUrl || '';

      if (!uploadedUrl) {
        throw new Error('Cover image upload failed.');
      }

      setValue('coverImageUrl', uploadedUrl, { shouldDirty: true, shouldValidate: true });
      setCoverUploadKey((prev) => prev + 1);
      showToast.success('Cover image uploaded successfully');
    } catch (error) {
      console.error(error);
      showToast.error('Failed to upload cover image. Please try again.');
    } finally {
      setIsUploadingCover(false);
    }
  }, [fileUploadMutation, setValue]);

  const handleRemoveCoverImage = useCallback(() => {
    setValue('coverImageUrl', '', { shouldDirty: true, shouldValidate: true });
    setCoverUploadKey((prev) => prev + 1);
  }, [setValue]);

  const submitWithStatus = async (data: PublicationFormOutput, saveStatus: 'DRAFT' | 'PUBLISH') => {
    const payload = {
      fileUrl: data.sourceType === 'UPLOAD' ? data.fileUrl : undefined,
      title: data.title.trim(),
      description: data.description,
      publicationCategory: data.publicationCategory,
      externalLink: data.sourceType === 'EXTERNAL' ? data.externalLink.trim() : undefined,
      homePageKeyResources: data.homePageKeyResources,
      isExternalSource: data.sourceType === 'EXTERNAL',
      legalFoundation: false,
      status: saveStatus,
      coverImageUrl: data.coverImageUrl.trim() ? data.coverImageUrl.trim() : null,
    };

    try {
      if (isEditMode && publicationId) {
        await updatePublication({ publicationId, data: payload });
        showToast.success(`Publication ${saveStatus === 'PUBLISH' ? 'published' : 'saved as draft'} successfully`);
      } else {
        await createPublication(payload);
        showToast.success(`Publication ${saveStatus === 'PUBLISH' ? 'published' : 'created as draft'} successfully`);
      }

      navigate('/admin/publications');
    } catch (error) {
      console.error(error);
      showToast.error('Failed to save publication. Please try again.');
    }
  };

  const handleBackClick = useCallback(() => {
    if (isDirty) {
      setShowUnsavedModal(true);
      return;
    }

    navigate('/admin/publications');
  }, [isDirty, navigate]);

  const uploadedDocumentName = localDocumentMeta?.name || existingDocumentName || 'Uploaded document';
  const uploadedDocumentSize = localDocumentMeta ? formatFileSize(localDocumentMeta.size) : 'Uploaded';
  const breadcrumbTitle = String(publicationData?.data?.title || title || 'Untitled Publication');

  if (isLoadingPublication && isEditMode) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <Loader size="md" variant="wine" message="Loading publication..." />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6 relative">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-white">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBackClick}
          className="!px-0 !py-0 text-[#047857] font-semibold hover:underline flex items-center gap-2"
          disabled={isBusy}
        >
          <ArrowLeft height={16} width={24} /> Back to Publications
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            loading={isSaving}
            disabled={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'DRAFT'))}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            loading={isSaving}
            disabled={!isValid || isBusy}
            onClick={() => setShowPublishModal(true)}
          >
            Publish
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl">
        <div className='border-b border-gray-200 px-4 py-4 md:px-7'>
          <Heading level={3} className="mb-3 leading-[1.1] font-semibold text-[#0F172A]">
            {isEditMode ? 'Edit Publication' : 'Create Publication'}
          </Heading>
          <nav className='flex items-center gap-2 text-sm'>
            <button
              type="button"
              onClick={() => navigate('/admin/publications')}
              className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={isBusy}
            >
              All Publications
            </button>
            {isEditMode ? (
              <>
                <span className='text-gray-400'>/</span>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/publications/${publicationId}`)}
                  className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={isBusy || !publicationId}
                >
                  {breadcrumbTitle}
                </button>
                <span className='text-gray-400'>/</span>
                <span className='text-gray-600'>Edit</span>
              </>
            ) : (
              <>
                <span className='text-gray-400'>/</span>
                <span className='text-gray-600'>New Publication</span>
              </>
            )}
          </nav>
        </div>

        <form className="space-y-6 p-6 md:p-8" onSubmit={(e) => e.preventDefault()}>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">1. Basic Information</Heading>
            <div className="space-y-4">
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Title"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.title?.message ? String(errors.title.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter publication title"
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-gray-700 ">Description</Label>
                    <RichTextEditor
                    {...field}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter publication description..."
                      disabled={isBusy}
                      contentAreaClassName="max-h-64"
                      
                    />
                    {errors.description?.message ? (
                      <Paragraph className="mt-1 text-xs text-red-600">{String(errors.description.message)}</Paragraph>
                    ) : null}
                  </div>
                )}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">2. Classification</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="publicationCategory"
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-gray-700">Category</Label>
                    <CustomSelect
                      value={field.value}
                      onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                      onBlur={field.onBlur}
                      options={categoryOptions}
                      placeholder={isLoadingCategories ? 'Loading categories...' : 'Select category'}
                      disabled={isLoadingCategories || isBusy}
                    />
                    {errors.publicationCategory?.message ? (
                      <Paragraph className="mt-1 text-xs text-red-600">{String(errors.publicationCategory.message)}</Paragraph>
                    ) : null}
                  </div>
                )}
              />

              <Controller
                name="sourceType"
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-gray-700">Source Type</Label>
                    <CustomSelect
                      value={field.value ?? 'UPLOAD'}
                      onChange={(value) => {
                        const nextValue = Array.isArray(value) ? value[0] ?? 'UPLOAD' : value;
                        field.onChange(nextValue);

                        if (nextValue === 'EXTERNAL') {
                          setValue('fileUrl', '', { shouldDirty: true, shouldValidate: true });
                          setLocalDocumentMeta(null);
                          setExistingDocumentName('');
                          setDocumentUploadKey((prev) => prev + 1);
                        } else {
                          setValue('externalLink', '', { shouldDirty: true, shouldValidate: true });
                        }
                      }}
                      onBlur={field.onBlur}
                      options={SOURCE_TYPE_OPTIONS}
                      disabled={isBusy}
                    />
                  </div>
                )}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">3. Source Content</Heading>

            {sourceType === 'UPLOAD' ? (
              <div className="space-y-4">
                <Controller
                  name="fileUrl"
                  control={control}
                  render={() => (
                    <div>
                      <Label className="mb-2 block text-sm font-medium text-gray-700">Document</Label>
                      <FileUpload
                        key={documentUploadKey}
                        ref={documentUploadRef}
                        label=""
                        placeholder=""
                        $maxSizeMB={50}
                        multiple={false}
                        allowedFileTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                        onChange={handleDocumentFileChange}
                        error={errors.fileUrl?.message ? String(errors.fileUrl.message) : undefined}
                        containerClassName="border border-dashed border-gray-300 min-h-[160px]"
                        disabled={isBusy}
                      >
                        <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 text-center">
                          <Paragraph className="text-sm font-semibold text-[#334155]">Upload publication file</Paragraph>
                          <Paragraph className="text-xs">Drag and drop or click to browse</Paragraph>
                          <Paragraph className="text-xs">Supported format: PDF, DOC, DOCX (max 50MB)</Paragraph>
                        </div>
                      </FileUpload>
                    </div>
                  )}
                />

                <div className="flex items-center gap-3">
                  <Button type="button" variant="secondary" onClick={handleBrowseDocument} disabled={isBusy}>
                    {isUploadingDocument ? 'Uploading...' : 'Select Document'}
                  </Button>
                  {fileUrl ? (
                    <Button type="button" variant="danger" onClick={handleRemoveDocument} disabled={isBusy}>
                      Remove
                    </Button>
                  ) : null}
                </div>

                {isUploadingDocument ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-amber-700" />
                      <Paragraph className="text-sm font-semibold text-amber-800">Uploading document. Please wait...</Paragraph>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-amber-200">
                      <div className="h-full w-1/2 animate-pulse rounded-full bg-amber-600" />
                    </div>
                  </div>
                ) : null}

                {fileUrl ? (
                  <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                    <Paragraph className="text-sm font-semibold text-gray-800">{uploadedDocumentName}</Paragraph>
                    <Paragraph className="text-xs text-gray-500">{uploadedDocumentSize}</Paragraph>
                  </div>
                ) : null}
              </div>
            ) : (
              <Controller
                name="externalLink"
                control={control}
                render={({ field }) => (
                  <Input
                    label="External Link"
                    required
                    type="url"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.externalLink?.message ? String(errors.externalLink.message) : undefined}
                    $marginbottom="0"
                    placeholder="https://example.com/document.pdf"
                  />
                )}
              />
            )}
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">4. Cover Image (Optional)</Heading>
            <div className="space-y-4">
              <FileUpload
                key={coverUploadKey}
                ref={coverUploadRef}
                label=""
                placeholder=""
                $maxSizeMB={5}
                multiple={false}
                allowedFileTypes={['image/*']}
                onChange={handleCoverImageChange}
                containerClassName="border border-dashed border-gray-300 min-h-[160px]"
                disabled={isBusy}
              >
                <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 text-center">
                  <Paragraph className="text-sm font-semibold text-[#334155]">Upload cover image</Paragraph>
                  <Paragraph className="text-xs">Drag and drop or click to browse</Paragraph>
                  <Paragraph className="text-xs">Supported: JPG, PNG, WEBP (max 5MB)</Paragraph>
                </div>
              </FileUpload>

              <div className="flex items-center gap-3">
                <Button type="button" variant="secondary" onClick={handleBrowseCover} disabled={isBusy}>
                  Select Image
                </Button>
                {coverImageUrl ? (
                  <Button type="button" variant="danger" onClick={handleRemoveCoverImage} disabled={isBusy}>
                    Remove
                  </Button>
                ) : null}
              </div>

              {isUploadingCover ? (
                <Paragraph className="text-sm text-gray-500">Uploading cover image...</Paragraph>
              ) : null}

              {coverImageUrl ? (
                <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-w-sm">
                  <img src={coverImageUrl} alt="Cover" className="h-44 w-full object-cover" />
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-xl border border-gray-300 px-4 py-6">
            <Controller
              name="homePageKeyResources"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Homepage Key Resource"
                  description="Feature this publication in key resources on the homepage."
                  disabled={isBusy}
                />
              )}
            />
          </section>
        </form>
      </div>

      <SaveDraftModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={() => {
          setShowUnsavedModal(false);
          handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
        }}
        onDiscard={() => {
          setShowUnsavedModal(false);
          navigate('/admin/publications');
        }}
        title="Save Publication?"
        subtitle="You have unsaved changes. Save as draft before leaving?"
        confirmButtonText="Save as Draft"
        discardButtonText="Discard"
      />

      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={() => {
          setShowPublishModal(false);
          handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))();
        }}
        title="Publish Publication?"
        subtitle="Are you sure you want to publish this publication?"
        infoText="This publication will be visible to the public once published."
        itemType="Publication"
        isLoading={isSaving}
      />
    </div>
  );
};

export default PublicationEditor;
